/**
 * Intake with photos and a signature on the phone (features "intakePhotos" and "phoneSignature",
 * owner's design of 2026-11-04).
 *
 * The workshop photographs the car at intake: five base views (front, left side, rear, right side,
 * dashboard with the odometer and the fuel level) and any number of damage close ups. The photos are
 * ordinary rows of public.photos with kind 'intake' and the view in intake_view. The customer may sign
 * the intake receipt (and later the handover) with a finger on the phone; the signature keeps a SHA-256
 * fingerprint of the receipt's content at that moment, so a later change of the receipt shows.
 *
 * Pure rules only (no browser, no database): the app, the database functions and the tests use them.
 */
import { legalProfile } from "./legal";

/** The five base views, in the order the guided capture asks for them. */
export const INTAKE_BASE_VIEWS = ["front", "left", "rear", "right", "dashboard"] as const;
export type IntakeBaseView = (typeof INTAKE_BASE_VIEWS)[number];
/** Any number of close ups of damage after the base views. */
export type IntakeView = IntakeBaseView | "damage";
export const INTAKE_VIEWS: readonly IntakeView[] = [...INTAKE_BASE_VIEWS, "damage"];

export function isIntakeView(v: unknown): v is IntakeView {
  return typeof v === "string" && (INTAKE_VIEWS as readonly string[]).includes(v);
}

export type IntakeMode = "offer" | "required";
export type IntakeSend = "ifEmail" | "never";
/** How long intake photos are kept after the work order was closed. */
export type IntakeRetention = "warrantyPlus1y" | "5y";

export interface IntakeSettings {
  mode: IntakeMode;
  /** At least this many intake photos before saving, in "required" mode. */
  min: number;
  send: IntakeSend;
  retention: IntakeRetention;
}

/** Keep equal to the defaults in settings.ts. */
export const INTAKE_PHOTO_MIN_DEFAULT = 5;
export const INTAKE_PHOTO_MIN_LIMIT = 20;

/** The workshop's intake settings from settings.general, with defaults and sane limits. */
export function intakeSettings(general: Record<string, unknown>): IntakeSettings {
  const min = Math.round(Number(general.intakePhotoMin));
  return {
    mode: general.intakePhotoMode === "required" ? "required" : "offer",
    min: Number.isFinite(min) && min >= 1 ? Math.min(min, INTAKE_PHOTO_MIN_LIMIT) : INTAKE_PHOTO_MIN_DEFAULT,
    send: general.intakePhotoSend === "never" ? "never" : "ifEmail",
    retention: general.intakePhotoRetention === "5y" ? "5y" : "warrantyPlus1y",
  };
}

/** What still blocks saving in "required" mode: null when nothing does. */
export function intakeMissing(s: Pick<IntakeSettings, "mode" | "min">, count: number): { needed: number; done: number } | null {
  if (s.mode !== "required" || count >= s.min) return null;
  return { needed: s.min, done: count };
}

/** The base views not photographed yet, in capture order. */
export function missingBaseViews(done: readonly (IntakeView | null | undefined)[]): IntakeBaseView[] {
  return INTAKE_BASE_VIEWS.filter((v) => !done.includes(v));
}

/**
 * The next view the guided capture asks for: the first base view that is neither done nor skipped
 * in this round, then damage close ups until the user is finished.
 */
export function nextIntakeView(done: readonly (IntakeView | null | undefined)[], skipped: readonly IntakeView[] = []): IntakeView {
  return INTAKE_BASE_VIEWS.find((v) => !done.includes(v) && !skipped.includes(v)) ?? "damage";
}

/**
 * The thumbnails of the printed receipt: base views in their order first, then the damage close
 * ups (oldest first), at most `max`; `more` says how many are only on the customer's link.
 */
export function intakeCollage<T extends { view: IntakeView | null; createdAt: string }>(photos: readonly T[], max = 8): { shown: T[]; more: number } {
  const rank = (p: T) => (p.view && p.view !== "damage" ? INTAKE_BASE_VIEWS.indexOf(p.view) : INTAKE_BASE_VIEWS.length);
  const sorted = [...photos].sort((a, b) => rank(a) - rank(b) || a.createdAt.localeCompare(b.createdAt));
  const shown = sorted.length > max ? sorted.slice(0, max - 1) : sorted;
  return { shown, more: sorted.length - shown.length };
}

/**
 * Last day the intake photos of a closed work order are kept (YYYY-MM-DD); after it the daily job
 * deletes them. "warrantyPlus1y": the statutory warranty of the workshop's country (Hungary 6 months
 * from handover) plus one year; "5y": five years after closing. Same rule as
 * public.intake_photo_keep_until in the database (a database test checks both).
 */
export function intakePhotoKeepUntil(closedAtIso: string, retention: IntakeRetention, legalCountry: string | null | undefined): string {
  const months = retention === "5y" ? 60 : (legalProfile(legalCountry ?? "HU").consumerWarranty?.months ?? 0) + 12;
  const [y, m, d] = closedAtIso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

export type SignatureKind = "intake" | "handover";

/** What the customer signs: the facts of the receipt at the moment of signing. */
export interface SignedContent {
  kind: SignatureKind;
  number: string;
  plate: string;
  km: number | null;
  customerName: string;
  request: string;
  expectedReadyAt: string | null;
  /** Ids of the intake photos the receipt refers to (sorted). */
  photoIds: string[];
  /** The time of signing on the phone (ISO). */
  signedAt: string;
  signerName: string;
  /** Handover only: the gross total of the closed work order in minor units. */
  gross?: number;
}

/** The content with fixed field order, trimmed texts and sorted photo ids. */
export function signedContent(c: SignedContent): SignedContent {
  const out: SignedContent = {
    kind: c.kind,
    number: c.number,
    plate: c.plate,
    km: c.km ?? null,
    customerName: c.customerName.trim(),
    request: c.request.trim(),
    expectedReadyAt: c.expectedReadyAt ?? null,
    photoIds: [...c.photoIds].sort(),
    signedAt: c.signedAt,
    signerName: c.signerName.trim(),
  };
  if (c.kind === "handover") out.gross = c.gross ?? 0;
  return out;
}

/** Canonical JSON: keys sorted at every level, no spaces, so the same content always gives the same text. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}

/** SHA-256 as 64 lower case hex characters (Web Crypto: browsers and Node). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** The fingerprint of a signed receipt. */
export function contentHash(c: SignedContent): Promise<string> {
  return sha256Hex(canonicalJson(signedContent(c)));
}

/** The first characters of the fingerprint as printed: "a3f9 7c21 5b0e". */
export function shortHash(hash: string, groups = 3): string {
  return (hash.match(/.{1,4}/g) ?? []).slice(0, groups).join(" ");
}

/** The customer's objection text: trimmed, 3 to 2000 characters. */
export function cleanObjection(text: string): string | null {
  const s = text.trim();
  return s.length >= 3 && s.length <= 2000 ? s : null;
}
